import React, { useState } from "react";
import { ChevronDown, Mail, Trash } from "lucide-react";
import { UserRole } from "../../../../services/UserService";
import { InvitedUser } from "../../../../models/UserModels";
import { formatRole } from "../../../../utils/RoleUtils";

const InviteMembersModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onProceed: (invitedUsers: InvitedUser[]) => void;
}> = ({ open, onClose, onProceed }) => {
  const roles = Object.values(UserRole).filter(
    (role) => role !== UserRole.Owner
  );

  const [role, setRole] = useState<string>(roles[0] || UserRole.Member);
  const [email, setEmail] = useState<string>("");
  const [users, setUsers] = useState<InvitedUser[]>([]);
  const [error, setError] = useState<string>("");

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+$/;
    return emailRegex.test(email) && !/[.@]$/.test(email);
  };

  const handleAdd = () => {
    if (!email || !isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    if (users.some((user) => user.email === email)) {
      setError("This email has already been added.");
      return;
    }

    if (users.length >= 4) {
      setError("You can only invite up to 4 members at once.");
      return;
    }

    setUsers([...users, { email, role }]);
    setEmail("");
    setError("");
  };

  const handleRemove = (index: number) => {
    setUsers(users.filter((_, i) => i !== index));
  };

  const handleProceed = () => {
    if (users.length === 0) {
      setError("Please add at least one user to invite.");
      return;
    }

    onProceed(users);
    setUsers([]);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
      <div className="w-full max-w-lg bg-background-paper dark:bg-background-paperDark rounded-lg shadow-xl p-6 relative">
        <div className="border-b border-divider dark:border-divider-dark pb-4 mb-6 relative">
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-primaryDark">
            Invite Members
          </h2>
          <p className="text-sm text-text-secondary dark:text-text-secondaryDark">
            Invite people to collaborate in your organization. Assign roles and
            send out invitations.
          </p>
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-0 right-0 text-text-secondary dark:text-text-secondaryDark hover:text-text-primary dark:hover:text-text-primaryDark transition"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-text-primary dark:text-text-primaryDark"
            >
              Email Address
            </label>
            <div className="relative mt-1">
              <input
                id="email"
                type="text"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[a-zA-Z0-9.@+]*$/.test(value) && !/\s/.test(value)) {
                    setEmail(value);
                  }
                }}
                className="w-full p-3 pl-10 border border-divider dark:border-divider-dark rounded-md bg-background-paper dark:bg-background-panelDark text-text-primary dark:text-text-primaryDark placeholder:text-text-secondary dark:placeholder:text-secondaryDark focus:ring-2 focus:ring-secondary outline-none"
              />
              <Mail className="absolute left-3 top-3.5 text-text-secondary dark:text-text-secondaryDark w-5 h-5" />
            </div>
            {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
          </div>

          <div>
            <label
              className="block text-sm font-medium text-text-primary dark:text-text-primaryDark mb-1"
              htmlFor="role"
            >
              Assign Role
            </label>
            <div className="relative">
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full p-3 pr-10 border border-divider dark:border-divider-dark rounded-md bg-background-paper dark:bg-background-panelDark text-text-primary dark:text-text-primaryDark focus:ring-2 focus:ring-secondary outline-none appearance-none"
              >
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {formatRole(r)}{" "}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-text-secondary dark:text-text-secondaryDark w-5 h-5 pointer-events-none" />
            </div>
          </div>

          <button
            onClick={handleAdd}
            className="w-full py-2 border-2 border-secondary text-secondary rounded-md transition font-medium 
             hover:bg-secondary hover:text-white"
          >
            Add to Invitation List
          </button>
        </div>

        {users.length > 0 && (
          <div className="mt-6">
            <h3 className="text-lg font-bold text-text-primary dark:text-text-primaryDark">
              Invitation List
            </h3>
            <ul className="space-y-3 mt-4">
              {users.map((user, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between p-3 bg-background-panel dark:bg-background-panelDark rounded-md"
                >
                  <div>
                    <p className="text-sm text-text-primary dark:text-text-primaryDark">
                      {user.email}
                    </p>
                    <p className="text-xs text-text-secondary dark:text-text-secondaryDark">
                      {formatRole(user.role)}{" "}
                      {/* ✅ Displays formatted role in UI */}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(index)}
                    className="text-red-500 hover:text-red-600 transition"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              onClick={handleProceed}
              className="w-full mt-4 py-2 bg-black text-white hover:bg-black/90 dark:bg-secondary dark:hover:bg-secondary-dark transition font-medium"
            >
              Send Invitations
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteMembersModal;
